import { action, computed, makeObservable, observable } from 'mobx';
import http from '../http';
import { ordersStore } from './OrdersStore';
import { uploadPictureFile } from './pictureUpload';
import { preprocessImage } from '../imagePreprocessor';

class PictureStore {
  pictures = []; // 保存文件信息
  tags = [];
  currentPic = null
  filter = "all"
  tagFilters = []
  photoLimitation = 0
  cover = null
  albumClaimed = false
  multiMode = false
  multiSelections = []
  selectedPicIds = []
  selections = []
  selectionId = null
  selectionCustomerName = ""


  constructor() {
    makeObservable(this, {
      photoLimitation: observable,
      cover:observable,
      filter: observable,
      tagFilters: observable,
      pictures: observable,
      tags: observable,
      currentPic: observable,
      albumClaimed: observable,
      multiMode:observable,
      multiSelections:observable,
      selectionId: observable,
      selectionCustomerName: observable,
      selectedPicIds: observable,
      selections: observable,
      setCurrentPic: action,
      addPicture: action,
      setPictures: action,
      selectedPictures: computed,
      unSelectedPictures: computed,
      setPhotoLimitation:action,
      previewPictures: computed,
      haveTagFilter:action,
      addTagFilter:action,
      removeTagFilter:action,
      beginUpload: action,
      removeUpload: action,
      updateProgress: action,
      setMultiMode: action,
      addMutiSelect: action,
      removeMutiSelect: action,
      toggleMultiSelect: action,
    });
  }

  setPhotoLimitation(limitation){
    this.photoLimitation = limitation
  }

  get previewPictures() {
    switch(this.filter){
      case "all":
        return this.allPicturesFiltedByTagIds
      case "selected":
        return this.selectedPictures
      case "unselected":
        return this.unSelectedPictures
      default:
        return this.pictures
    }
  }

  get allPicturesFiltedByTagIds() {
    if(this.tagFilters.length === 0){
      return this.pictures
    }
    return this.pictures.filter(picture => {
      return this.tagFilters.every(tagId => picture.tags.some(tag => tag.id === tagId));
    })
  }
  get selectedPictures() {
    return this.allPicturesFiltedByTagIds.filter(f => this.selectedPicIds.includes(f.id));
  }

  get unSelectedPictures() {
    return this.allPicturesFiltedByTagIds.filter(f => !this.selectedPicIds.includes(f.id));
  }

  setPreviewPicturesFilter(filter){
    this.filter = filter
  }

  haveTagFilter(tagId) {
    return this.tagFilters.includes(tagId)
  }

  addTagFilter(tagId) {
    if(!this.tagFilters.includes(tagId)){
      this.tagFilters.push(tagId)
    }
  }
  removeTagFilter(tagId) {
    this.tagFilters = this.tagFilters.filter(f => f !== tagId)
  }

  beginUpload(uuid, originalFileName, progress = 0, progressMessage = '准备上传') {
    if (!this.pictures.find(f => f.id === uuid)) {
      this.pictures.unshift({ id: uuid, progress, progressMessage, originalFileName });
    } else {
      this.updateProgress(uuid, progress, progressMessage);
    }
  }

  removeUpload(uuid) {
    this.pictures = this.pictures.filter(f => f.id !== uuid);
  }


  setTags(tags) {
    this.tags = tags
  }

  setPictures(pictures) {
    this.pictures = pictures
  }

  hasPictureNamed(originalFileName, excludeId = null) {
    const normalizedName = String(originalFileName || '').trim()
    if (!normalizedName) return false
    return this.pictures.some(picture =>
      picture.id !== excludeId
      && String(picture.originalFileName || '').trim() === normalizedName
    )
  }

  isPictureSelected(pictureId) {
    return this.selectedPicIds.includes(pictureId)
  }

  setSelectedPicIds(pictureIds) {
    this.selectedPicIds = pictureIds || []
    this.pictures.forEach(picture => {
      if (this.selectedPicIds.includes(picture.id)) {
        picture.claimed = true;
      } else {
        picture.claimed = false;
      }
    })
  }

  setSelectionId(selectionId) {
      this.selectionId = selectionId
      let selection = this.selections.find(s => s.id === selectionId)
      if(selection){
       this.setSelectedPicIds(selection?.pictureIds)
       this.selectionCustomerName = selection?.customerName || ""
      }
  }

  setSelections(selections) {
    this.selections = selections || []
    if(this.selections.length > 0) {
      let defaultSelected = this.selections[0]
      this.setSelectionId(defaultSelected.id)
     
      this.setAlbumClaimed(true)
    }
  }


  async addPicture(file, uuid, albumId, maxDimension, sourceFolderName = null) {
    const originalFileName = file.name.trim();

    if (this.hasPictureNamed(originalFileName, uuid)) {
      this.removeUpload(uuid);
      return 'exist';
    }

    this.beginUpload(uuid, originalFileName, 3, '准备处理');

    try {
      this.updateProgress(uuid, 7, '正在标准化图片');
      const processed = await preprocessImage(file, { maxDimension });
      this.updateProgress(uuid, 14, '图片处理完成');
      return await this.uploadToServer(uuid, albumId, processed, sourceFolderName);
    } catch (error) {
      console.error(`图片上传失败：${originalFileName}`, error);
      this.removeUpload(uuid);
      throw error;
    }
  }

  async uploadToServer(uuid, albumId, processed, sourceFolderName = null){
    const result = await uploadPictureFile({
      uuid,
      albumId,
      file: processed.file,
      originalFile: processed.originalFile,
      dimensions: processed.dimensions,
      sourceFolderName,
      orderId: ordersStore.currentOrder?.id,
      onProgress: (uploadId, progress, message) => this.updateProgress(uploadId, progress, message),
    });

    if(result.status === 'exist'){
      this.pictures = this.pictures.filter(f => f.id !== uuid)
      return "exist"
    }

    const finalIndex = this.pictures.findIndex(f => f.id === uuid);
    if (finalIndex !== -1) {
      this.updateProgress(uuid, 100, '上传完成');
      this.pictures[finalIndex] = result.picture
    }

    return "success"
  }

  updateProgress(uuid, progress, progressMessage) {
    const picture = this.pictures.find(f => f.id === uuid);
    if (picture) {
      if (typeof progress === 'number') picture.progress = progress;
      if (progressMessage) picture.progressMessage = progressMessage;
    }
  }

  setCurrentPic(picture) {
    this.currentPic = picture;
  }

  setCover(pictureId) {
    this.cover = pictureId
  }

  setAlbumClaimed(claimed){
    this.albumClaimed = claimed
  }

  setMultiMode(mode){
    this.multiMode = mode
    if(!mode){
      this.multiSelections = []
    }
  }

  addMutiSelect(pictureId){
    const exists = this.multiSelections.some(item => String(item) === String(pictureId))
    if (!exists) this.multiSelections.push(pictureId)
  }

  removeMutiSelect(pictureId){
    this.multiSelections = this.multiSelections.filter(item => String(item) !== String(pictureId))
  }

  toggleMultiSelect(pictureId){
    const selected = this.multiSelections.some(item => String(item) === String(pictureId))
    if (selected) {
      this.removeMutiSelect(pictureId)
    } else {
      this.addMutiSelect(pictureId)
    }
  }

  async deletePictrus(orderId, pictureIds){
    const response = await http.post('/user/picture/delete', { orderId:orderId ,pictureIds: pictureIds });
    if(response.data.success){
      pictureIds.forEach(pictureId => {
        this.pictures = this.pictures.filter(f => f.id !== pictureId)
      });
      if (pictureIds.some(pictureId => String(pictureId) === String(this.cover))) {
        this.cover = this.pictures[0]?.id || null
      }
      this.multiSelections = []
    }
   
  }
  
  reset(){
    this.pictures = []
    this.currentPic = null
    this.filter = "all"
    this.photoLimitation = 0
    this.cover = null
    this.albumClaimed = false
    this.multiMode = false
    this.multiSelections = []
    this.selectedPicIds = []
    this.selections = []
  }
}

const pictureStore = new PictureStore();

export { pictureStore };
